import { forwardRef, useState } from "react";
import styles from "./input.module.less";
import { TAutoCompleteValues, TInputFieldTypeValues, TInputModeValues } from "../../../types/form-element-types";
import InputElementTopSection from "../components/input-element-top-section/input-element-top-section";
import InputSkeletonLoader from "../components/input-skeleton-loader/input-skeleton-loader";
import { Checkbox } from "../checkbox-group/checkbox-group";
import { useTranslation } from "react-i18next";

export type TInputStandardProps = {
  type?: TInputFieldTypeValues;
  className?: string;
  inputMode?: TInputModeValues;
  value?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;

  autoFocus?: boolean;
  autoComplete?: TAutoCompleteValues;
  errorText?: string;
  isValid?: boolean;
  isReadOnly?: boolean;
  pattern?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  minLength?: number;
  maxLength?: number;
  inputClassName?: string;
  onBlur?: CallableFunction;
  onKeyUp?: CallableFunction;
  onKeyDown?: CallableFunction;
  fieldRef?: any;
  isLoading?: boolean;
  min?: string | number | undefined;
  max?: string | number | undefined;
  onClickClear?: CallableFunction;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

type TInputInternalProps = TInputStandardProps & {
  iconRight?: string;
  onClickIconRight?: CallableFunction;
};

const InternalInput = forwardRef<HTMLInputElement, TInputInternalProps>(
  (
    {
      name,
      value,
      isValid,
      errorText,
      isDisabled,
      isReadOnly,
      isRequired,
      label,
      placeholder,
      helperText,
      autoComplete,
      inputMode,
      type,
      className,
      inputClassName,
      iconRight,
      minLength,
      maxLength,
      pattern,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      onKeyUp,
      isLoading,
      onClickIconRight,
      min,
      max,
    },
    forwardedRef,
  ) => {
    const displayError = () => {
      if (isRequired && isValid === false) {
        return true;
      }

      if (isValid === false && value) {
        return true;
      }

      return false;
    };

    const onClickIconRightInternal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onClickIconRight && typeof onClickIconRight === "function") {
        onClickIconRight();
      }
    };

    const onChangeInternal = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange && typeof onChange === "function") {
        onChange(event);
      }
    };

    const onFocusInternal = (event: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus && typeof onFocus === "function") {
        onFocus(event);
      }
    };

    const onBlurInternal = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(event);
      }
    };
    /** Native input onKeyUp */
    const onKeyUpInternal = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (onKeyUp && typeof onKeyUp === "function") {
        onKeyUp(event);
      }
    };
    /** Native input onKeyDown */
    const onKeyDownInternal = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown && typeof onKeyDown === "function") {
        onKeyDown(event);
      }
    };

    const getInputMode = () => {
      if (inputMode) {
        return inputMode;
      }

      if (type === "number") {
        return "numeric";
      }

      return undefined;
    };

    const displayErrorText = displayError();
    const hasIcon = iconRight ? styles.withIcon : "";
    const isIconButtonDisabled = isDisabled || isReadOnly;

    return (
      <div className={[styles.wrapper, className, isDisabled && styles.disabled, "space-mb-md"].join(" ")}>
        <div className={styles.innerWrapper}>
          <InputElementTopSection
            label={label}
            helperText={helperText}
            errorText={displayErrorText ? errorText : undefined}
            htmlFor={name}
          />
          <div className={styles.inputWrapper}>
            {isLoading ? (
              <InputSkeletonLoader />
            ) : (
              <>
                {displayErrorText && <div className={styles.errorIndicator} />}
                <input
                  className={[
                    styles.input,
                    inputClassName,
                    displayErrorText ? styles.error : "",
                    hasIcon ? styles.withIcon : "",
                  ].join(" ")}
                  aria-required={isRequired ? true : false}
                  ref={forwardedRef}
                  id={name ? name : undefined}
                  type={type ? type : "text"}
                  value={value ? value : ""}
                  name={name}
                  pattern={pattern}
                  disabled={isDisabled}
                  inputMode={getInputMode()}
                  placeholder={placeholder}
                  autoComplete={autoComplete ? autoComplete : "off"}
                  onChange={(event) => onChangeInternal(event)}
                  onFocus={(event) => onFocusInternal(event)}
                  onBlur={(event) => onBlurInternal(event)}
                  onKeyUp={(event) => onKeyUpInternal(event)}
                  onKeyDown={(event) => onKeyDownInternal(event)}
                  minLength={minLength}
                  maxLength={maxLength}
                  readOnly={isReadOnly}
                  min={min}
                  max={max}
                />
              </>
            )}
            {iconRight && (
              <button
                disabled={isIconButtonDisabled}
                onClick={onClickIconRightInternal}
                className={[styles.icon, iconRight].join(" ")}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);

InternalInput.displayName = "Input";

const InputText = (props: TInputStandardProps) => {
  return <InternalInput {...props} />;
};

export type TInputSearchProps = TInputStandardProps & {
  onClickClear?: CallableFunction;
};

const InputSearch = (props: TInputSearchProps) => {
  return (
    <InternalInput
      {...props}
      iconRight={props.value ? "ui-icon-close" : "ui-icon-search"}
      onClickIconRight={props.value ? props.onClickClear : undefined}
    />
  );
};

export type TInputPasswordProps = Omit<TInputStandardProps, "type">;

const InputPassword = (props: TInputPasswordProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setShowPassword(true);
    } else {
      setShowPassword(false);
    }
  };

  return (
    <div className={`${styles.inputPasswordWrapper} space-mb-md`}>
      <InternalInput {...props} type={showPassword ? "text" : "password"} />
      <Checkbox
        label={t("INPUT_PASSWORD_SHOW_PASSWORD")}
        name={"password"}
        isChecked={showPassword}
        onChange={onChange}
      />
    </div>
  );
};

export const Input = {
  Text: InputText,
  Password: InputPassword,
  Search: InputSearch,
};
